import { useLayoutEffect, useRef, useState } from 'react';

import { DateTime, parseDate } from '@jebel/utils';

import { formatPostDate } from 'shared/utils/date';

export interface UseRelativeDateOptions {
  /**
   * Refresh the relative time each second, minute or hour depending on the difference with now.
   * @default false
   */
  refresh?: boolean;

  /**
   * Sets the relative date to the given `value`.
   * @default DateTime.now()
   */
  relativeTo?: unknown;

  /**
   * Formats the date.
   * @param date The date to format (Luxon DateTime).
   * @returns The formatted date.
   */
  formatter?: (date: DateTime) => string;
}

const TO_SECONDS = 1000;
const TO_MINUTES = 60 * TO_SECONDS;

const NO_REFRESH_INTERVAL = 0;
const NO_LABEL = '';

/**
 * Transform the given `value` into relative date and format using `Intl`.
 * @param value Given date on the past, present or future on any accepted format (`Date`, ISO, SQL, ...).
 * @example ```ts
 * const publishedAt = useRelativeDate(post.publishedAt)
 * ```
 */
export function useRelativeDate(value: unknown, options?: UseRelativeDateOptions) {
  const withRefresh = options?.refresh ?? false;
  const formatter = options?.formatter ?? formatPostDate;

  const interval = useRef<unknown>();
  const datetime = parseDate(value);

  const refreshLabel = () => {
    const format = formatter(datetime);
    setFormatted(format);
  };

  const [formatted, setFormatted] = useState<string>(NO_LABEL);
  const [every, setEvery] = useState(1 * TO_SECONDS);

  const refreshFormat = () => {
    const diff = datetime.diffNow();

    const minutes = Math.abs(diff.as('minutes'));
    const hours = Math.abs(diff.as('hours'));

    if (!withRefresh || hours > 1) {
      setEvery(NO_REFRESH_INTERVAL);
      return;
    }

    if (minutes > 1) {
      setEvery(1 * TO_MINUTES);
    }

    refreshLabel();
  };

  useLayoutEffect(() => {
    refreshLabel();

    if (!withRefresh || every === 0) {
      return;
    }

    interval.current = setInterval(refreshFormat, every);

    return () => {
      clearInterval(interval.current as number);
    };
  }, [every, withRefresh]);

  return formatted;
}
